import { Expr } from "../expr/ast";
import { collectReferences, guardedReferences } from "../expr/references";
import { inferType } from "../expr/typecheck";
import { refreshIssues } from "../registry/freshness";
import { BindingDecl, EffectClass } from "../registry/kinds";
import { KindRegistry } from "../registry/registry";
import {
  TypeRef,
  isAssignable,
  resolvePathType,
  typeToString,
} from "../types/type-system";
import { GraphAnalysis, analyzeGraph } from "./analysis";
import {
  AutomationDefinition,
  DEFAULT_PORT,
  ERROR_PORT,
  TRIGGER_NODE_ID,
  automationDefinitionSchema,
  stepFieldName,
} from "./automation";
import {
  AutomationTypes,
  alwaysProducesOutput,
  buildAutomationTypes,
} from "./context-types";

export type Severity = "error" | "warning";

export interface Diagnostic {
  severity: Severity;
  code: string;
  message: string;
  nodeId?: string;
  bindingKey?: string;
  /**
   * The automation can't run at all (an unknown or outdated kind, a config that
   * doesn't match its kind, a broken graph), not just fails a check.
   */
  blocksExecution?: true;
}

export interface ValidationResult {
  diagnostics: Diagnostic[];
  analysis: GraphAnalysis;
  types: AutomationTypes | null;
  ok: boolean;
}

export function validateAutomation(
  automation: AutomationDefinition,
  registry: KindRegistry,
): ValidationResult {
  const diagnostics: Diagnostic[] = [];
  const push = (d: Diagnostic) => diagnostics.push(d);
  const block = (d: Diagnostic) => push({ ...d, blocksExecution: true });

  const parsed = automationDefinitionSchema.safeParse(automation);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      block({
        severity: "error",
        code: "schema",
        message: `${issue.path.join(".")}: ${issue.message}`,
      });
    }
  }

  const triggerKind = registry.triggers.get(automation.trigger.kind);
  if (!triggerKind) {
    block({
      severity: "error",
      code: "unknown-trigger",
      nodeId: TRIGGER_NODE_ID,
      message: automation.trigger.kind
        ? `Unknown trigger ${automation.trigger.kind}`
        : "Choose what starts this automation",
    });
  } else {
    if (triggerKind.version !== automation.trigger.kindVersion) {
      block({
        severity: "error",
        code: "kind-version",
        nodeId: TRIGGER_NODE_ID,
        message: `Trigger ${triggerKind.id} is v${triggerKind.version}, automation has v${automation.trigger.kindVersion}`,
      });
    }
    const config = triggerKind.parse(automation.trigger.config);
    if (!config.ok) {
      const report = config.malformed ? block : push;
      for (const issue of config.issues) {
        report({
          severity: "error",
          code: "config",
          nodeId: TRIGGER_NODE_ID,
          message: issue.path
            ? `${issue.path}: ${issue.message}`
            : issue.message,
        });
      }
    }
    if (config.ok) {
      for (const issue of refreshIssues(
        triggerKind.refreshFor(automation.trigger.config),
        triggerKind.payload(automation.trigger.config),
      )) {
        push({
          severity: "error",
          code: "trigger-refresh",
          nodeId: TRIGGER_NODE_ID,
          message: issue.message,
        });
      }
    }
    if (triggerKind.availability) {
      push({
        severity: "warning",
        code: "trigger-gated",
        nodeId: TRIGGER_NODE_ID,
        message: triggerKind.availability.gated,
      });
    }
  }

  const ids = new Set<string>();
  // Outputs live under steps.<field>; two ids that sanitize to the same field would share it.
  const fields = new Map<string, string>();
  let fieldCollision = false;
  for (const node of automation.nodes) {
    if (node.id === TRIGGER_NODE_ID || ids.has(node.id)) {
      block({
        severity: "error",
        code: "duplicate-id",
        nodeId: node.id,
        message: `Duplicate node id ${node.id}`,
      });
    } else {
      const field = stepFieldName(node.id);
      const other =
        fields.get(field) ??
        (field === TRIGGER_NODE_ID ? TRIGGER_NODE_ID : undefined);
      if (other !== undefined) {
        fieldCollision = true;
        block({
          severity: "error",
          code: "duplicate-id",
          nodeId: node.id,
          message: `Node ids ${other} and ${node.id} both read as steps.${field}; rename one`,
        });
      }
      fields.set(field, node.id);
    }
    ids.add(node.id);
    const kind = registry.steps.get(node.kind);
    if (!kind) {
      block({
        severity: "error",
        code: "unknown-kind",
        nodeId: node.id,
        message: `Unknown step ${node.kind}`,
      });
      continue;
    }
    if (kind.version !== node.kindVersion) {
      block({
        severity: "error",
        code: "kind-version",
        nodeId: node.id,
        message: `${kind.title} is v${kind.version}, node has v${node.kindVersion}`,
      });
    }
    if (node.disabled && !kind.skippable) {
      push({
        severity: "warning",
        code: "disabled-not-skippable",
        nodeId: node.id,
        message: `${kind.title} can't be skipped, so this step still runs while disabled`,
      });
    }
    const config = kind.parse(node.config);
    const report = !config.ok && config.malformed ? block : push;
    for (const issue of config.issues ?? []) {
      report({
        severity: issue.severity ?? "error",
        code: "config",
        nodeId: node.id,
        message: issue.path ? `${issue.path}: ${issue.message}` : issue.message,
      });
    }
  }

  const seenPorts = new Set<string>();
  for (const edge of automation.edges) {
    const fromOk = edge.from === TRIGGER_NODE_ID || ids.has(edge.from);
    if (!fromOk) {
      push({
        severity: "error",
        code: "edge",
        message: `Edge from unknown node ${edge.from}`,
      });
      continue;
    }
    if (!ids.has(edge.to)) {
      block({
        severity: "error",
        code: "edge",
        nodeId: edge.from,
        message: `Edge to unknown node ${edge.to}`,
      });
    }
    const key = `${edge.from}:${edge.port}`;
    if (seenPorts.has(key)) {
      push({
        severity: "error",
        code: "edge",
        nodeId: edge.from,
        message: `Port "${edge.port}" has more than one edge`,
      });
    }
    seenPorts.add(key);
    const ports = portsOf(automation, registry, edge.from);
    if (ports && !ports.includes(edge.port)) {
      push({
        severity: "error",
        code: "edge",
        nodeId: edge.from,
        message: `Unknown port "${edge.port}"`,
      });
    }
  }

  const analysis = analyzeGraph(automation);
  if (analysis.cycle) {
    block({
      severity: "error",
      code: "cycle",
      nodeId: analysis.cycle[0],
      message: `Steps form a loop: ${analysis.cycle.join(" → ")}`,
    });
  }
  for (const node of automation.nodes) {
    if (!analysis.reachable.has(node.id)) {
      push({
        severity: "warning",
        code: "unreachable",
        nodeId: node.id,
        message: "This step is not connected to the trigger",
      });
    }
  }

  let types: AutomationTypes | null = null;
  if (triggerKind && !analysis.cycle && !fieldCollision) {
    // Unfinished trigger settings (no event chosen yet) are reported above. Steps are
    // still typed against the trigger's default shape so they can be edited meanwhile.
    const triggerOk = triggerKind.parse(automation.trigger.config).ok;
    const typed = triggerOk
      ? automation
      : {
          ...automation,
          trigger: {
            ...automation.trigger,
            config: triggerKind.defaultConfig(),
          },
        };
    types = buildAutomationTypes(typed, registry, analysis);
    checkBindings(typed, registry, types, analysis, push, triggerOk);
    checkPolicies(automation, types, push);
  }

  return {
    diagnostics,
    analysis,
    types,
    ok: !diagnostics.some((d) => d.severity === "error"),
  };
}

function portsOf(
  automation: AutomationDefinition,
  registry: KindRegistry,
  nodeId: string,
): string[] | undefined {
  if (nodeId === TRIGGER_NODE_ID) {
    return [DEFAULT_PORT];
  }
  const node = automation.nodes.find((n) => n.id === nodeId);
  const kind = node ? registry.steps.get(node.kind) : undefined;
  if (!node || !kind || !kind.parse(node.config).ok) {
    return undefined;
  }
  return [
    ...kind.ports(node.config).map((p) => p.id),
    ...(hasErrorPort(kind) ? [ERROR_PORT] : []),
  ];
}

/** Steps that perform effects can fail, so they get an error port. */
export function hasErrorPort(kind: { effects: EffectClass[] }): boolean {
  return kind.effects.some((e) => e !== "pure");
}

function checkExpr(
  types: AutomationTypes,
  root: TypeRef,
  expr: Expr,
  expected: TypeRef | undefined,
  where: { nodeId: string; bindingKey: string; label: string },
  maybeMissing: ReadonlySet<string>,
  push: (d: Diagnostic) => void,
): void {
  const result = inferType(expr, { ts: types.ts, root });
  if (!result.ok) {
    push({
      severity: "error",
      code: "type",
      nodeId: where.nodeId,
      bindingKey: where.bindingKey,
      message: `${where.label}: ${result.error}`,
    });
    return;
  }
  if (expected && !isAssignable(types.ts, result.type, expected)) {
    push({
      severity: "error",
      code: "type",
      nodeId: where.nodeId,
      bindingKey: where.bindingKey,
      message: `${where.label}: expected ${typeToString(expected)}, got ${typeToString(result.type)}`,
    });
  }
  const guarded = new Set(
    guardedReferences(expr).map((p) => JSON.stringify(p)),
  );
  for (const path of collectReferences(expr)) {
    const [first, second] = path;
    if (guarded.has(JSON.stringify(path))) {
      continue;
    }
    if (first?.field === "steps" && second && maybeMissing.has(second.field)) {
      push({
        severity: "warning",
        code: "maybe-missing",
        nodeId: where.nodeId,
        bindingKey: where.bindingKey,
        message: `${where.label}: uses data from a step that may not run before this one`,
      });
    }
  }
}

function checkBindings(
  automation: AutomationDefinition,
  registry: KindRegistry,
  types: AutomationTypes,
  analysis: GraphAnalysis,
  push: (d: Diagnostic) => void,
  checkTrigger: boolean,
): void {
  const triggerKind = registry.triggers.get(automation.trigger.kind);
  if (triggerKind && checkTrigger) {
    const filter = triggerKind.filter(automation.trigger.config);
    if (filter) {
      checkExpr(
        types,
        types.policyContext,
        filter,
        { kind: "named", name: "Boolean", nullable: true },
        {
          nodeId: TRIGGER_NODE_ID,
          bindingKey: "filter",
          label: "Trigger filter",
        },
        new Set(),
        push,
      );
    }
    for (const binding of triggerKind.bindings(automation.trigger.config)) {
      checkExpr(
        types,
        types.policyContext,
        binding.expr,
        binding.expected,
        {
          nodeId: TRIGGER_NODE_ID,
          bindingKey: binding.key,
          label: binding.label,
        },
        new Set(),
        push,
      );
    }
  }
  for (const node of automation.nodes) {
    const kind = registry.steps.get(node.kind);
    if (
      !kind ||
      !kind.parse(node.config).ok ||
      !analysis.reachable.has(node.id)
    ) {
      continue;
    }
    const maybeMissing = new Set<string>();
    for (const upstream of analysis.sometimes.get(node.id) ?? []) {
      const info = types.outputs.get(upstream);
      const upstreamNode = automation.nodes.find((n) => n.id === upstream);
      const upstreamKind = upstreamNode
        ? registry.steps.get(upstreamNode.kind)
        : undefined;
      const guaranteed =
        upstreamNode !== undefined &&
        upstreamKind !== undefined &&
        alwaysProducesOutput(
          automation,
          analysis,
          node.id,
          upstreamNode,
          upstreamKind,
        );
      if (info && !guaranteed) {
        maybeMissing.add(info.field);
      }
    }
    const contextual = kind.checkInContext(node.config, {
      ts: types.ts,
      root: types.contextAt(node.id),
    });
    for (const issue of contextual) {
      push({
        severity: issue.severity ?? "error",
        code: "type",
        nodeId: node.id,
        bindingKey: issue.path,
        message: issue.message,
      });
    }
    if (contextual.some((i) => i.severity !== "warning")) {
      continue;
    }
    const bindings: BindingDecl[] = kind.bindings(node.config);
    for (const binding of bindings) {
      checkExpr(
        types,
        types.contextAt(node.id),
        binding.expr,
        binding.expected,
        { nodeId: node.id, bindingKey: binding.key, label: binding.label },
        maybeMissing,
        push,
      );
    }
  }
}

function checkPolicies(
  automation: AutomationDefinition,
  types: AutomationTypes,
  push: (d: Diagnostic) => void,
): void {
  if (automation.policies.entryCondition) {
    checkExpr(
      types,
      types.policyContext,
      automation.policies.entryCondition,
      { kind: "named", name: "Boolean", nullable: true },
      {
        nodeId: TRIGGER_NODE_ID,
        bindingKey: "policies.entryCondition",
        label: "Entry condition",
      },
      new Set(),
      push,
    );
  }
}

/** Whether evaluating the expressions needs a resolver (a data lookup). */
export function readsLazyData(
  types: AutomationTypes,
  root: TypeRef,
  exprs: Expr[],
): boolean {
  return exprs.some((expr) =>
    collectReferences(expr).some((path) => {
      const resolved = resolvePathType(types.ts, root, path);
      return resolved.ok && resolved.lazySegments.length > 0;
    }),
  );
}
